import { ColDef } from "@ag-grid-community/core";
import Decimal from "decimal.js";
import { useFeeChargeEventStatsRetrieveSuspense } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { FeeOffsetApplicationAPIData } from "gen/model/feeOffsetApplicationAPIData";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxActions } from "shared/components/table/box/TableBoxActions";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTable } from "shared/components/table/maybern/MaybernTable";
import { MaybernTableColumnTypes } from "shared/components/table/maybern/MaybernTableColumnTypes";
import { MaybernTableDataTypeDefinitions } from "shared/components/table/maybern/MaybernTableDataTypeDefinitions";
import { decimalOrZero, decimalZero } from "shared/utils/DecimalUtils";

interface FeePostApplyOffsetsSummaryTableProps {
  fundFamilyId: string;
  feeChargeEventId: string;
  feeOffsetApplications: FeeOffsetApplicationAPIData[];
}

interface FeePostApplyOffsetsSummaryRowData {
  id: string;
  row_header: string;
  fund_id_to_amount: Record<string, Decimal>;
  fund_family_amount: Decimal;
}

export const FeePostApplyOffsetsSummaryTable: React.FC<
  FeePostApplyOffsetsSummaryTableProps
> = ({ fundFamilyId, feeChargeEventId, feeOffsetApplications }) => {
  const { t } = useTranslation(["fees", "common"]);

  const { data: feeChargeEventStats } = useFeeChargeEventStatsRetrieveSuspense(
    fundFamilyId,
    feeChargeEventId,
  );

  const { data: fundList } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });

  const grossFeesRowData = useMemo<FeePostApplyOffsetsSummaryRowData>(() => {
    const fundIdToAmount = fundList.reduce(
      (acc: Record<string, Decimal>, fund) => {
        acc[fund.id!] =
          fund.id! in feeChargeEventStats.fund_id_to_fee_stats
            ? decimalOrZero(
                feeChargeEventStats.fund_id_to_fee_stats[fund.id!].fees,
              )
            : decimalZero();
        return acc;
      },
      {},
    );
    const fundFamilyAmount = Object.values(fundIdToAmount).reduce(
      (a, b) => a.plus(b),
      decimalZero(),
    );
    return {
      id: "gross_fees",
      row_header: t("fees:grossFeesAfterFeeBreaks"),
      fund_id_to_amount: fundIdToAmount,
      fund_family_amount: fundFamilyAmount,
    };
  }, [feeChargeEventStats.fund_id_to_fee_stats, fundList, t]);

  const offsetsRowData = useMemo<FeePostApplyOffsetsSummaryRowData>(() => {
    const fundIdToAmount: Record<string, Decimal> = fundList.reduce(
      (acc: Record<string, Decimal>, fund) => {
        const fundApplicationAmounts = feeOffsetApplications.map((it) =>
          decimalOrZero(it.fund_id_to_fund_application_amount[fund.id!]),
        );
        acc[fund.id!] = fundApplicationAmounts.reduce(
          (a, b) => a.minus(b),
          decimalZero(),
        );
        return acc;
      },
      {},
    );
    const fundFamilyAmount = Object.values(fundIdToAmount).reduce(
      (a, b) => a.plus(b),
      decimalZero(),
    );
    return {
      id: "fee_offsets",
      row_header: t("fees:offsets.title"),
      fund_id_to_amount: fundIdToAmount,
      fund_family_amount: fundFamilyAmount,
    };
  }, [feeOffsetApplications, fundList, t]);

  const rowData: FeePostApplyOffsetsSummaryRowData[] = useMemo(
    () => [grossFeesRowData, offsetsRowData],
    [grossFeesRowData, offsetsRowData],
  );

  const columnDefs = useMemo<ColDef<FeePostApplyOffsetsSummaryRowData>[]>(
    () => [
      {
        field: "row_header",
        aggFunc: () => t("fees:applyOffsets.totalNetFeesRemaining"),
        headerName: "",
        initialWidth: 400,
      },
      ...fundList.map(
        (fund): ColDef<FeePostApplyOffsetsSummaryRowData> => ({
          field: `fund_id_to_amount.${fund.id}`,
          headerName: fund.legal_name,
          cellDataType: "money",
          initialWidth: 300,
          cellClass: "mb-cell-value-type-money",
          headerClass: "mb-header-value-type-money",
          resizable: true,
        }),
      ),
      {
        field: "fund_family_amount",
        headerName: t("common:fundFamily"),
        cellDataType: "money",
        initialWidth: 300,
        cellClass: "mb-cell-value-type-money",
        headerClass: "mb-header-value-type-money",
      },
    ],

    [t, fundList],
  );

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>{t("common:summary")}</TableBoxTitle>
        <TableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <MaybernTable<FeePostApplyOffsetsSummaryRowData>
          getRowId={(data) => data.data.id}
          grandTotalRow="bottom"
          noRowsOverlayComponentParams={{
            emptyStateProps: {
              title: t("fees:offsets.empty.title"),
            },
          }}
          dataTypeDefinitions={MaybernTableDataTypeDefinitions}
          columnTypes={MaybernTableColumnTypes}
          columnDefs={columnDefs}
          rowData={rowData}
        />
      </TableBoxBody>
    </TableBox>
  );
};
